import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";

import VisualEditor from "./VisualEditor";
import VariablePalette from "./VariablePalette";
import PreviewPanel from "./PreviewPanel";
import VersionHistory from "./VersionHistory";
import RequestVariableDialog from "./RequestVariableDialog";
import {
  auditLine,
  CHANNEL_LABELS,
  messageTemplateApi,
} from "../../utils/messageTemplateApi";
import { insertAtCaret, visualEditingBlocker } from "../../utils/mustacheHtml";

const useStyles = makeStyles({
  panel: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  meta: { fontSize: "13px", color: "#475467" },
  source: { fontFamily: "monospace", fontSize: "13px", lineHeight: 1.45 },
  tabs: { minHeight: "44px", borderBottom: "1px solid #d0d5dd" },
  tab: { minHeight: "44px", textTransform: "none" },
  saveBar: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
    padding: "12px 16px",
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    borderTop: "1px solid #d0d5dd",
    marginTop: "16px",
  },
  grow: { flex: "1 1 260px" },
  touch: { minHeight: "44px" },
  danger: { minHeight: "44px", color: "#b42318", borderColor: "#b42318" },
  progressWrap: {
    display: "flex",
    justifyContent: "center",
    padding: "48px 0",
  },
});

const EMPTY = { subject: "", htmlTemplate: "" };

const formFrom = (content) => ({
  subject: content?.subject || "",
  htmlTemplate: content?.htmlTemplate || "",
  changeNote: "",
  variableRequests: [],
});

/** Baseline for add/create: never equal to a form, so the content can always be submitted. */
const UNSAVED = { ...formFrom(EMPTY), htmlTemplate: null };

const sameContent = (a, b) =>
  a.subject === b.subject && a.htmlTemplate === b.htmlTemplate;

/**
 * Subject, body, preview and history of one language of a message template.
 *
 * - `edit`: loads the language itself and saves, deletes, reactivates and restores it.
 * - `add` / `create`: starts from `initialContent` and hands the content to `onSubmit`, which
 *   adds the language or creates the template and navigates away.
 */
const VariantEditor = ({
  mode,
  templateId,
  templateName,
  templateActive,
  localeId,
  definition,
  initialContent,
  templateRevision,
  onSubmit,
  submitLabel,
  submitBlocker,
  onChanged,
  onDirtyChange,
}) => {
  const classes = useStyles();
  const isEdit = mode === "edit";

  const [variant, setVariant] = useState(null);
  const [form, setForm] = useState(() => formFrom(initialContent || EMPTY));
  const [baseline, setBaseline] = useState(() =>
    isEdit ? formFrom(EMPTY) : UNSAVED,
  );
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState([]);
  const [conflict, setConflict] = useState(null);
  const [bodyMode, setBodyMode] = useState("source");
  const [focusTarget, setFocusTarget] = useState("body");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [confirm, setConfirm] = useState(null);
  const [requesting, setRequesting] = useState(false);

  // add/create: whether the admin has changed the content since the starting point loaded.
  const touched = useRef(false);
  const visualRef = useRef(null);
  const sourceRef = useRef(null);
  const subjectRef = useRef(null);

  const variables = definition?.variables || [];
  const isHtml = definition ? definition.html : true;
  const hasSubject = definition ? definition.hasSubject : true;

  // ---- loading ---------------------------------------------------------------------------

  const adopt = useCallback((loaded) => {
    setVariant(loaded);
    const next = formFrom(loaded);
    setForm(next);
    // A legacy row converted for editing is "dirty": saving completes the conversion.
    setBaseline(
      loaded.convertedFromLegacy ? { ...next, htmlTemplate: null } : next,
    );
    setConflict(null);
    setSaveErrors([]);
    setHistoryKey((k) => k + 1);
  }, []);

  const load = useCallback(() => {
    if (!isEdit) return;
    setLoading(true);
    messageTemplateApi
      .variant(templateId, localeId)
      .then(adopt)
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [adopt, isEdit, templateId, localeId]);

  useEffect(() => {
    load();
  }, [load]);

  // A template-wide change (description, delete, reactivate) adds an entry to this history but
  // leaves this language's content and revision alone: refresh the history only.
  const firstTemplateRevision = useRef(templateRevision);
  useEffect(() => {
    if (templateRevision === firstTemplateRevision.current) return;
    firstTemplateRevision.current = templateRevision;
    setHistoryKey((k) => k + 1);
  }, [templateRevision]);

  // add/create: a new starting point replaces the content, unless the admin already typed.
  useEffect(() => {
    if (isEdit || !initialContent) return;
    if (touched.current) return;
    setForm((prev) => ({
      ...formFrom(initialContent),
      changeNote: prev.changeNote,
      variableRequests: prev.variableRequests,
    }));
    // Only a new starting point should reset the content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, initialContent]);

  const dirty = !sameContent(form, baseline);
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // ---- body mode --------------------------------------------------------------------------

  const visualBlocker = useMemo(
    () =>
      isHtml ? visualEditingBlocker(form.htmlTemplate) : "Plain text template.",
    [isHtml, form.htmlTemplate],
  );

  // Default to visual only when it is lossless for the body just loaded.
  const loadedBody = isEdit
    ? variant?.htmlTemplate
    : initialContent?.htmlTemplate;
  useEffect(() => {
    setBodyMode(
      isHtml && loadedBody != null && !visualEditingBlocker(loadedBody)
        ? "visual"
        : "source",
    );
  }, [loadedBody, variant?.revision, isHtml]);

  // ---- preview ----------------------------------------------------------------------------

  const previewRequest = useMemo(
    () => ({
      templateName,
      localeId,
      subject: hasSubject ? form.subject : null,
      htmlTemplate: form.htmlTemplate,
    }),
    [templateName, localeId, form.subject, form.htmlTemplate, hasSubject],
  );

  const refreshPreview = useCallback(() => {
    if (!previewRequest.templateName) return;
    setPreviewLoading(true);
    messageTemplateApi
      .preview(previewRequest)
      .then(setPreview)
      .catch((e) => toast.error(e.message))
      .finally(() => setPreviewLoading(false));
  }, [previewRequest]);

  useEffect(() => {
    const handle = setTimeout(refreshPreview, 700);
    return () => clearTimeout(handle);
  }, [refreshPreview]);

  const sendTest = async () => {
    setSending(true);
    try {
      const result = await messageTemplateApi.testEmail(previewRequest);
      toast.success(`Test email sent to ${result.sentTo}.`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  // ---- editing ----------------------------------------------------------------------------

  const setField = (field) => (event) => {
    if (field !== "changeNote") touched.current = true;
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const insertToken = (token, plainForSubject) => {
    touched.current = true;
    if (focusTarget === "subject" && hasSubject) {
      // Subjects are plain text headers rendered unescaped; a simple tag is always right.
      const { value, caret } = insertAtCaret(
        subjectRef.current,
        form.subject,
        plainForSubject,
      );
      setForm((prev) => ({ ...prev, subject: value }));
      requestAnimationFrame(() => {
        subjectRef.current?.focus();
        subjectRef.current?.setSelectionRange(caret, caret);
      });
      return;
    }
    if (bodyMode === "visual" && visualRef.current) {
      visualRef.current.insertToken(token);
      return;
    }
    const { value, caret } = insertAtCaret(
      sourceRef.current,
      form.htmlTemplate,
      token,
    );
    setForm((prev) => ({ ...prev, htmlTemplate: value }));
    requestAnimationFrame(() => {
      sourceRef.current?.focus();
      sourceRef.current?.setSelectionRange(caret, caret);
    });
  };

  const insertVariable = (variable) =>
    insertToken(variable.token, `{{${variable.name}}}`);

  /** A variable the code does not supply yet: insert it and file a request on save. */
  const requestVariable = ({ tag, description }) => {
    setRequesting(false);
    insertToken(`{{${tag}}}`, `{{${tag}}}`);
    setForm((prev) => ({
      ...prev,
      variableRequests: [
        ...prev.variableRequests.filter((r) => r.tag !== tag),
        { tag, description },
      ],
    }));
  };

  const switchMode = (_, next) => {
    if (next === "visual" && visualBlocker) {
      toast.info(visualBlocker);
      return;
    }
    setBodyMode(next);
  };

  const handleFailure = (e) => {
    if (e.status === 409) {
      setConflict(e.message);
      return;
    }
    setSaveErrors(String(e.message || "Save failed").split("\n"));
  };

  const content = () => ({
    subject: hasSubject ? form.subject : null,
    htmlTemplate: form.htmlTemplate,
    changeNote: form.changeNote,
    variableRequests: form.variableRequests,
  });

  const save = async () => {
    setSaving(true);
    setSaveErrors([]);
    try {
      if (!isEdit) {
        // The parent navigates away on success; mark clean first so nothing prompts.
        const body = content();
        setBaseline({ ...form });
        await onSubmit(body);
        return;
      }
      const saved = await messageTemplateApi.updateVariant(
        templateId,
        localeId,
        { ...content(), revision: variant.revision },
      );
      adopt(saved);
      toast.success(`Saved as revision ${saved.revision}.`);
      if (onChanged) onChanged();
    } catch (e) {
      if (!isEdit) setBaseline(UNSAVED);
      handleFailure(e);
    } finally {
      setSaving(false);
    }
  };

  const runConfirmed = async () => {
    const { action, note } = confirm;
    setConfirm(null);
    try {
      const result =
        action === "delete"
          ? await messageTemplateApi.removeVariant(
              templateId,
              localeId,
              variant.revision,
              note,
            )
          : await messageTemplateApi.reactivateVariant(
              templateId,
              localeId,
              variant.revision,
              note,
            );
      adopt(result);
      toast.success(
        action === "delete" ? "Language deleted." : "Language reactivated.",
      );
      if (onChanged) onChanged();
    } catch (e) {
      if (e.status === 409) setConflict(e.message);
      else toast.error(e.message);
    }
  };

  // ---- render -----------------------------------------------------------------------------

  if (loading) {
    return (
      <Box className={classes.progressWrap}>
        <CircularProgress aria-label="Loading language" />
      </Box>
    );
  }

  const readOnly = isEdit && (!variant || !variant.active || !templateActive);
  const unresolved = preview?.unresolvedVariables || [];
  const submitDisabled =
    saving || readOnly || !dirty || Boolean(!isEdit && submitBlocker);

  return (
    <Box>
      <Box display="flex" flexDirection="column" style={{ gap: 12 }} mb={2}>
        {isEdit && variant && (
          <Typography className={classes.meta}>
            {localeId} · revision {variant.revision} · created{" "}
            {auditLine(variant.createdAt, variant.createdByName)} · last change{" "}
            {auditLine(variant.updatedAt, variant.updatedByName)}
          </Typography>
        )}
        {isEdit && variant && !variant.active && templateActive && (
          <Alert severity="warning">
            This language is deleted. Recipients who use it get the next
            matching language instead. Reactivate it to edit.
          </Alert>
        )}
        {isEdit && !templateActive && (
          <Alert severity="warning">
            The whole template is deleted, so no language is sent. Reactivate
            the template to edit.
          </Alert>
        )}
        {variant?.convertedFromLegacy && (
          <Alert severity="info">
            This language was stored in the old %s format and has been converted
            to named variables for editing. Check the preview, then save to
            complete the conversion.
          </Alert>
        )}
        {variant?.legacyConversionFailed && (
          <Alert severity="error">
            This language is in the old %s format and could not be converted
            automatically. Replace each %s in the source with the matching
            variable, in the order listed, then save.
          </Alert>
        )}
        {conflict && (
          <Alert
            severity="error"
            action={
              isEdit ? (
                <Button
                  color="inherit"
                  className={classes.touch}
                  onClick={load}
                >
                  Reload latest
                </Button>
              ) : undefined
            }
          >
            {conflict}
            {isEdit &&
              " Reloading discards your unsaved edits; copy them first if you need them."}
          </Alert>
        )}
        {saveErrors.length > 0 && (
          <Alert severity="error" role="alert">
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {saveErrors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </Alert>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Paper
            className={classes.panel}
            component="section"
            aria-label={`Content in ${localeId}`}
          >
            {hasSubject && (
              <TextField
                id="mt-variant-subject"
                label="Subject"
                variant="outlined"
                value={form.subject}
                onChange={setField("subject")}
                onFocus={() => setFocusTarget("subject")}
                disabled={readOnly}
                inputRef={subjectRef}
                helperText="Plain text; variables allowed. Leave empty to use the sender's default subject."
                inputProps={{ maxLength: 500 }}
              />
            )}

            <Box>
              {isHtml ? (
                <Tabs
                  value={bodyMode}
                  onChange={switchMode}
                  className={classes.tabs}
                  aria-label="Body editor mode"
                  indicatorColor="primary"
                >
                  <Tab
                    value="visual"
                    label="Visual"
                    className={classes.tab}
                    aria-disabled={Boolean(visualBlocker)}
                    title={visualBlocker || "Rich-text editing"}
                  />
                  <Tab
                    value="source"
                    label="HTML source"
                    className={classes.tab}
                  />
                </Tabs>
              ) : (
                <Typography variant="body2" gutterBottom>
                  {CHANNEL_LABELS[definition?.channel] || "Plain text"} — not
                  HTML.
                </Typography>
              )}
              {isHtml && bodyMode === "source" && visualBlocker && (
                <Typography
                  variant="body2"
                  style={{ color: "#475467", margin: "8px 0" }}
                >
                  {visualBlocker}
                </Typography>
              )}
              <Box mt={1}>
                {isHtml &&
                bodyMode === "visual" &&
                !readOnly &&
                !visualBlocker ? (
                  <VisualEditor
                    ref={visualRef}
                    value={form.htmlTemplate}
                    onChange={(html) => {
                      touched.current = true;
                      setForm((prev) => ({ ...prev, htmlTemplate: html }));
                    }}
                    variables={variables}
                    onFocus={() => setFocusTarget("body")}
                    ariaLabel="Message body"
                  />
                ) : (
                  <TextField
                    id="mt-variant-body"
                    label={
                      isHtml ? "Body (HTML with Mustache variables)" : "Body"
                    }
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={isHtml ? 18 : 4}
                    maxRows={40}
                    value={form.htmlTemplate}
                    onChange={setField("htmlTemplate")}
                    onFocus={() => setFocusTarget("body")}
                    disabled={readOnly}
                    inputRef={sourceRef}
                    InputProps={{ className: classes.source }}
                    inputProps={{ spellCheck: false }}
                  />
                )}
              </Box>
            </Box>

            <VariablePalette
              variables={variables}
              onInsert={insertVariable}
              onRequest={readOnly ? null : () => setRequesting(true)}
              requested={form.variableRequests}
              targetLabel={
                focusTarget === "subject" && hasSubject ? "subject" : "body"
              }
              declared={definition ? definition.declared : true}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper className={classes.panel}>
            <PreviewPanel
              preview={preview}
              loading={previewLoading}
              onRefresh={refreshPreview}
              onSendTest={sendTest}
              sending={sending}
              hasSubject={hasSubject}
              unresolvedVariables={unresolved}
            />
          </Paper>
          {isEdit && variant && (
            <Paper className={classes.panel} style={{ marginTop: 16 }}>
              <VersionHistory
                templateId={templateId}
                localeId={localeId}
                currentRevision={variant.revision}
                active={Boolean(variant.active && templateActive)}
                onRestored={(restored) => {
                  adopt(restored);
                  if (onChanged) onChanged();
                }}
                refreshKey={historyKey}
              />
            </Paper>
          )}
        </Grid>
      </Grid>

      <Paper className={classes.saveBar} component="section" aria-label="Save">
        <TextField
          id="mt-variant-change-note"
          label="Change note (optional)"
          variant="outlined"
          size="small"
          className={classes.grow}
          value={form.changeNote}
          onChange={setField("changeNote")}
          disabled={readOnly && !(isEdit && variant && !variant.active)}
          inputProps={{ maxLength: 500 }}
        />
        {isEdit &&
          variant &&
          templateActive &&
          (variant.active ? (
            <Button
              variant="outlined"
              className={classes.danger}
              onClick={() =>
                setConfirm({ action: "delete", note: form.changeNote })
              }
            >
              Delete {localeId}
            </Button>
          ) : (
            <Button
              variant="outlined"
              className={classes.touch}
              onClick={() =>
                setConfirm({ action: "reactivate", note: form.changeNote })
              }
            >
              Reactivate {localeId}
            </Button>
          ))}
        {!isEdit && submitBlocker && (
          <Typography variant="body2" style={{ color: "#475467" }}>
            {submitBlocker}
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          className={classes.touch}
          onClick={save}
          disabled={submitDisabled}
        >
          {saving ? "Saving…" : submitLabel || (isEdit ? "Save" : "Create")}
        </Button>
      </Paper>

      <RequestVariableDialog
        open={requesting}
        variables={variables}
        onCancel={() => setRequesting(false)}
        onConfirm={requestVariable}
      />

      <Dialog
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        aria-labelledby="mt-variant-confirm-title"
      >
        <DialogTitle id="mt-variant-confirm-title">
          {confirm?.action === "delete"
            ? `Delete the ${localeId} language?`
            : `Reactivate the ${localeId} language?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.action === "delete"
              ? "Recipients who use this language get the next matching one instead (a related language, then en-US). It stays in history and can be reactivated."
              : `Recipients who use ${localeId} get this version again.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button className={classes.touch} onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button
            className={classes.touch}
            color={confirm?.action === "delete" ? "secondary" : "primary"}
            variant="contained"
            onClick={runConfirmed}
            autoFocus
          >
            {confirm?.action === "delete" ? "Delete" : "Reactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

VariantEditor.propTypes = {
  mode: PropTypes.oneOf(["edit", "add", "create"]).isRequired,
  templateId: PropTypes.number,
  templateName: PropTypes.string,
  templateActive: PropTypes.bool,
  localeId: PropTypes.string,
  definition: PropTypes.shape({
    html: PropTypes.bool,
    hasSubject: PropTypes.bool,
    declared: PropTypes.bool,
    channel: PropTypes.string,
    variables: PropTypes.array,
  }),
  initialContent: PropTypes.shape({
    subject: PropTypes.string,
    htmlTemplate: PropTypes.string,
  }),
  /** The template's own revision; a change refreshes the history, not the content. */
  templateRevision: PropTypes.number,
  onSubmit: PropTypes.func,
  submitLabel: PropTypes.string,
  submitBlocker: PropTypes.string,
  onChanged: PropTypes.func,
  onDirtyChange: PropTypes.func,
};

VariantEditor.defaultProps = {
  templateId: null,
  templateName: "",
  templateActive: true,
  localeId: "",
  definition: null,
  initialContent: null,
  templateRevision: null,
  onSubmit: null,
  submitLabel: null,
  submitBlocker: null,
  onChanged: null,
  onDirtyChange: null,
};

export default VariantEditor;
